// ─────────────────────────────────────────────────────────────────────────────
// nodesService.js — the ONLY place the Nodes page talks to the backend.
//
//   READS
//     GET    /enterprise-nodes                  → the fleet
//     GET    /enterprise-nodes/summary          → the four KPI tiles
//     GET    /enterprise-nodes/:id              → one node
//     GET    /enterprise-nodes/:id/logs/stream  → live log tail (this module builds
//                                                 the URL; useNodeLogs owns the
//                                                 EventSource)
//   WRITES                                        (Admin + Security Admin only)
//     POST   /enterprise-nodes                  → register a node, get its token
//     PUT    /enterprise-nodes/:id              → rename and/or re-address
//     DELETE /enterprise-nodes/:id              → permanent, irreversible purge
//     PATCH  /enterprise-nodes/:id/active       → hide from routing / restore
//     POST   /enterprise-nodes/:id/reprobe      → refresh status now
//
// MIGRATED from /nodes and /api/partner/v1, both of which the backend removed:
//   /nodes/*                            → /enterprise-nodes/*
//   PATCH /partner/v1/cloud-nodes/:id   → PATCH /enterprise-nodes/:id/active
// `/enterprise-nodes` is now the single node surface: it serves the owning
// company's authority fields (name, active, enrolled, ports, geo, live user
// count) joined with the latest metrics snapshot, so one call feeds the whole
// page. Every path here is relative to BASE_URL, which already ends in
// /api/enterprise/v1.
//
// NOT here, on purpose:
//   • The Naoris super-admin tier (/api/naoris/v1/*) is a separate JWT universe
//     with its own secret and its own three-layer gate. This app has never called
//     it and must not start from here — nothing about it changed.
//   • Mesh client enrollment tokens (POST /internal/mesh/users/:userId/
//     enrollment-token) cannot be called from a browser at all: /internal/* is
//     gated by the X-Service-Token shared secret, and shipping that secret to a
//     SPA would hand every visitor the whole internal surface. See MESH-TOKENS
//     in ../Pages/Nodes/README.md.
//
// It does three things beyond fetching, and they are why no component on the
// page imports `api` directly:
//
//   1. SHAPE. Callers get the UI shape (toUiNode) and never see the wire format,
//      so a backend rename is a change here and nowhere else.
//
//   2. ERRORS. Every failure — unreachable server, 500, expired session, a body
//      that isn't the shape we expect — becomes one NodesError whose `message` is
//      safe to put on screen. Raw server text never reaches the UI: "Error 500"
//      or an upstream stack trace is not something a user of this page should
//      read. The technical detail stays on `err.detail`, for the console.
//
//      WRITES ARE THE ONE EXCEPTION, and deliberately so — see toWriteError. A
//      rejected create/update is a statement about what the admin just typed
//      ("publicIp must be a routable public IP"), and hiding it behind a generic
//      message would leave them with a form they cannot fix.
//
//   3. TOTAL RESULTS. A resolved call always yields a usable value — never
//      undefined, never a half-parsed body — so callers need no defensive checks
//      on the happy path.
//
// Display formatting deliberately does NOT live here; it is a UI concern and
// lives in ../Pages/Nodes/format.js.
// ─────────────────────────────────────────────────────────────────────────────
import api, { BASE_URL, TokenStorage } from "./api";

// ── Errors ────────────────────────────────────────────────────────────────────

/** An error already phrased for display. `detail` keeps the original for logs. */
export class NodesError extends Error {
  constructor(message, { status = null, detail = null } = {}) {
    super(message);
    this.name = "NodesError";
    this.status = status;
    this.detail = detail;
  }
}

// One message per failure class. Deliberately free of status codes and server
// text — see note 2 above.
const MESSAGES = {
  offline: "Can't reach the server. Check your connection and try again.",
  auth:    "Your session has expired. Please sign in again.",
  denied:  "You don't have permission to view node monitoring.",
  server:  "The server couldn't return this data right now. Try again in a moment.",
  unknown: "Something went wrong loading node data.",
};

// Write-side equivalents. "Denied" differs from the read one because reaching a
// write means the user could already READ the page — what they lack is
// read_write on node_monitoring (Admin / Security Admin), not visibility.
const WRITE_MESSAGES = {
  denied:   "You don't have permission to change nodes. This needs an Admin or Security Admin.",
  missing:  "That node no longer exists. Refresh the list and try again.",
  conflict: "That change conflicts with the node's current state. Refresh the list and try again.",
  server:   "The server couldn't apply this change right now. Try again in a moment.",
  unknown:  "Something went wrong applying this change.",
};

// Node Core download. These two failures are specific enough that the generic
// write messages would actively mislead:
//   • 409 on /config is not "refresh and retry" — the node is RUNNING, and the
//     answer is to stop it or not need a token at all.
//   • 502 on /binary-url is not the caller's fault and retrying won't help; it
//     means the backend can't reach the build bucket (an IAM/config problem an
//     operator has to fix), so say so rather than inviting a pointless retry.
const DOWNLOAD_MESSAGES = {
  online:      "This node is online, so it doesn't need a new enrollment token. Stop the node first if you're re-deploying it.",
  unavailable: "The Node Core binary isn't available for download right now. This is a server configuration issue — contact your administrator.",
  platform:    "That platform isn't available yet. Only Linux builds are published today.",
};

/**
 * Turns anything thrown by api.js into a NodesError safe to render.
 * api.js converts fetch's TypeError into a message but no status, so a
 * status-less error is treated as a connectivity failure.
 */
function toNodesError(err) {
  const status = err?.status ?? null;
  let message;

  if      (status === 401)  message = MESSAGES.auth;
  else if (status === 403)  message = MESSAGES.denied;
  else if (status >= 500)   message = MESSAGES.server;
  else if (status === null) message = MESSAGES.offline;
  else                      message = MESSAGES.unknown;

  // Keep the real cause reachable for debugging, and out of the rendered text.
  console.warn("[nodesService]", status ?? "network", err?.message ?? err);

  return new NodesError(message, { status, detail: err?.message ?? String(err) });
}

/**
 * The write counterpart of toNodesError.
 *
 * A 400 is passed THROUGH rather than replaced: on these endpoints it is always
 * a validation verdict on the admin's own input, and it is the only thing that
 * tells them which field to fix. api.js puts the headline on `message` and the
 * per-field list on `details` (["publicIp must be a routable public IP …"]), so
 * prefer `details` and fall back to the headline.
 *
 * Everything else stays generic for the same reason reads do — a 500's text is
 * an implementation detail, not a message for an operator.
 */
function toWriteError(err) {
  const status = err?.status ?? null;

  if (status === 400) {
    const details = Array.isArray(err?.details) ? err.details.filter(Boolean) : [];
    const message = details.length > 0
      ? details.join(" ")
      : (err?.message || WRITE_MESSAGES.unknown);
    return new NodesError(message, { status, detail: err?.message ?? String(err) });
  }

  let message;
  if      (status === 401)  message = MESSAGES.auth;
  else if (status === 403)  message = WRITE_MESSAGES.denied;
  else if (status === 404)  message = WRITE_MESSAGES.missing;
  else if (status === 409 || status === 410) message = WRITE_MESSAGES.conflict;
  else if (status >= 500)   message = WRITE_MESSAGES.server;
  else if (status === null) message = MESSAGES.offline;
  else                      message = WRITE_MESSAGES.unknown;

  console.warn("[nodesService:write]", status ?? "network", err?.message ?? err);

  return new NodesError(message, { status, detail: err?.message ?? String(err) });
}

/** Runs an api call, normalising both thrown errors and the 401 null return. */
async function call(fn, mapError = toNodesError) {
  let res;
  try {
    res = await fn();
  } catch (err) {
    throw mapError(err);
  }
  // api.js resolves to null after expiring a dead session (it has already
  // dispatched "auth:expired" and the app is redirecting). Surface it as an
  // error so no caller mistakes it for "the server said there is nothing".
  if (res === null || res === undefined) {
    throw new NodesError(MESSAGES.auth, { status: 401, detail: "empty response" });
  }
  return res;
}

/** call() with the write error mapping. */
const write = (fn) => call(fn, toWriteError);

// ── Shape ─────────────────────────────────────────────────────────────────────

/**
 * The UI shape of a node — every consumer on the page reads this, never the raw
 * payload.
 *
 * Two halves arrive in one object and both are represented here:
 *   AUTHORITY — the company's own `nodes` row: name, ip, ports, geo, active,
 *               enrolled, trust_score, uptime_percent, and `users` (the live
 *               session count). Always present.
 *   METRICS   — the latest snapshot: status_label, latency, load, circuits,
 *               alerts. NULL — never absent — until the collector has a snapshot
 *               for that node, so the page's "missing → —" rule always has a
 *               field to test.
 *
 * `name` and `uptime_percent` are real fields now. They used to be hardcoded to
 * null here because the old /nodes read surface dropped them; /enterprise-nodes
 * serves both off the authority row, so the fallbacks that existed for them
 * (nodeLabel → node_id, fmtUptimeCell → duration) are now genuine last resorts
 * rather than the normal case.
 */
function toUiNode(raw) {
  return {
    node_id:               raw.node_id,
    node_type:             raw.node_type ?? "relay",
    name:                  raw.name ?? null,
    ip:                    raw.ip,
    region:                raw.region,
    country:               raw.country,
    country_code:          raw.country_code ?? null,
    latitude:              raw.latitude,
    longitude:             raw.longitude,
    online:                raw.online,
    // backend statusLabel returns lowercase — capitalise for the UI
    status:                raw.status_label
                             ? raw.status_label.charAt(0).toUpperCase() + raw.status_label.slice(1)
                             : (raw.online ? "Optimal" : "Offline"),
    trust_score:           raw.trust_score,
    latency_avg_ms:        raw.latency_avg_ms,
    load_percent:          raw.load_percent,
    avg_network_load_bps:  raw.avg_network_load_bps,
    uptime_seconds:        raw.uptime_seconds,
    uptime_percent:        raw.uptime_percent ?? null,
    active_circuits:       raw.active_circuits,
    active_onion_circuits: raw.active_onion_circuits,
    pq_active:             raw.pq_active,
    nist_level:            raw.nist_level,
    alerts:                Array.isArray(raw.alerts) ? raw.alerts : [],
    alerts_count:          raw.alerts_count ?? 0,
    metrics_at:            raw.metrics_at,

    // ── Authority fields the old /nodes surface didn't carry ────────────────
    // `users` is the REAL per-node user count (from node_sessions). It is not
    // interchangeable with active_circuits, which counts circuits, comes from the
    // metrics half, and is null whenever there is no snapshot — one user can hold
    // several circuits.
    users:                 raw.users ?? null,
    // Routing visibility (PATCH /:id/active), not liveness. A node can be
    // enrolled and online yet inactive, in which case routing skips it.
    active:                raw.active ?? null,
    enrolled:              raw.enrolled ?? null,
    enrollment_expires_at: raw.enrollment_expires_at ?? null,
    api_port:              raw.api_port ?? null,
    circuit_port:          raw.circuit_port ?? null,
    onion_port:            raw.onion_port ?? null,
    is_relay:              raw.is_relay ?? null,
    is_exit:               raw.is_exit ?? null,
    last_probe_at:         raw.last_probe_at ?? null,
    created_at:            raw.created_at ?? null,
  };
}

// ── Reads ─────────────────────────────────────────────────────────────────────

/**
 * The fleet, already in UI shape.
 *
 * The endpoint has returned both a bare array and { nodes, total } across
 * versions, so both are accepted and no caller has to care. Anything else — a
 * string, a null, an error page that happened to parse as JSON — yields [] with
 * a console warning rather than throwing: an odd body is not worth a dead page.
 *
 * Only `country` and `online` are server-side filters. The old `node_type`
 * parameter is gone: /enterprise-nodes doesn't accept it, and sending it would
 * silently return an UNFILTERED fleet — worse than not offering the filter,
 * because the caller would believe it applied. node_type arrives on every row,
 * so filter on it client-side (useNodeFilters) if it's needed.
 *
 * @param {{ country?: string, online?: boolean }} opts
 * @returns {Promise<object[]>}
 * @throws  {NodesError}
 */
export async function fetchNodes(opts = {}) {
  const params = new URLSearchParams();
  if (opts.country !== undefined) params.set("country", opts.country);
  if (opts.online  !== undefined) params.set("online",  String(opts.online));
  const qs = params.toString() ? `?${params}` : "";

  const res = await call(() => api.get(`/enterprise-nodes${qs}`));
  const list = Array.isArray(res) ? res : res.nodes;
  if (!Array.isArray(list)) {
    console.warn("[nodesService] /enterprise-nodes returned an unexpected body", res);
    return [];
  }
  return list.map(toUiNode);
}

/**
 * The KPI summary:
 *   { active_nodes: { value, total }, total_users, enterprise_users,
 *     normal_users, avg_network_load_pct, total_bandwidth_bps,
 *     total_bandwidth_gbps }
 * @returns {Promise<object>}
 * @throws  {NodesError}
 */
export async function fetchNodesSummary() {
  return call(() => api.get("/enterprise-nodes/summary"));
}

/**
 * One node in UI shape, or null when it doesn't exist / isn't this company's.
 * @param {string} nodeId
 * @returns {Promise<object|null>}
 * @throws  {NodesError}
 */
export async function fetchNode(nodeId) {
  try {
    const res = await call(() => api.get(`/enterprise-nodes/${encodeURIComponent(nodeId)}`));
    return toUiNode(res);
  } catch (err) {
    if (err instanceof NodesError && err.status === 404) return null;
    throw err;
  }
}

// ── Writes ────────────────────────────────────────────────────────────────────

/**
 * Register a node. The backend generates the node_id, picks the default ports and
 * DETECTS geo from the IP — none of those are ours to send. The body is exactly
 * { publicIp, name }.
 *
 * The 201 carries a SINGLE-USE enrollment token, valid ~1h. It is worth copying
 * off the screen that receives it, but it is NOT the only chance any more:
 * fetchNodeCoreConfig re-serves the same token while it is still valid, and issues
 * a fresh one once it has expired. (It is stored in plaintext, not hashed — which
 * is what makes re-serving possible.)
 *
 * A create can succeed WITH warnings — an unreachable IP or an unresolvable geo
 * lookup still creates the node. `warnings` is advisory, never an error, so
 * callers must not treat a non-empty array as a failure.
 *
 * @param {{ publicIp: string, name: string }} input
 * @returns {Promise<{ node_id: string, enrollment_token: string, expires_at: string,
 *                     gateway_url: string, reachable: boolean, geo: object,
 *                     warnings: string[] }>}
 * @throws  {NodesError} 400 carries the field-level reason — show it verbatim.
 */
export async function createNode({ publicIp, name }) {
  const res = await write(() =>
    api.post("/enterprise-nodes", { publicIp: publicIp?.trim(), name: name?.trim() }),
  );
  return { ...res, warnings: Array.isArray(res.warnings) ? res.warnings : [] };
}

/**
 * Rename and/or re-address a node. PUT (not PATCH) and both fields are required
 * every time — the endpoint takes the same pair as create.
 *
 * publicIp IS mutable. Changing it re-addresses the node: circuits are torn down
 * at the old address, open sessions are closed (those users must reconnect), and
 * the node gets one heartbeat interval of clone-detection grace. Identity
 * survives — same node_id, same public key, no new enrollment token. Sending the
 * node's CURRENT ip is a plain rename: no geo lookup, no teardown, no grace.
 *
 * Geo is always re-detected server-side and can never be supplied by the client,
 * so the stored location cannot end up disagreeing with the address.
 *
 * @param {string} nodeId
 * @param {{ publicIp: string, name: string }} input
 * @returns {Promise<object>} the node in UI shape, plus ip_changed, previous_ip,
 *   geo, warnings — and when the IP changed, reachable, sessions_closed and
 *   heartbeat_grace_until.
 * @throws  {NodesError}
 */
export async function updateNode(nodeId, { publicIp, name }) {
  const res = await write(() =>
    api.put(`/enterprise-nodes/${encodeURIComponent(nodeId)}`, {
      publicIp: publicIp?.trim(),
      name:     name?.trim(),
    }),
  );
  return {
    ...toUiNode(res),
    ip_changed:            res.ip_changed ?? false,
    previous_ip:           res.previous_ip ?? null,
    geo:                   res.geo ?? null,
    reachable:             res.reachable ?? null,
    sessions_closed:       res.sessions_closed ?? null,
    heartbeat_grace_until: res.heartbeat_grace_until ?? null,
    warnings:              Array.isArray(res.warnings) ? res.warnings : [],
  };
}

/**
 * PERMANENTLY delete a node. Irreversible: the row, its sessions and its bound
 * enrollment tokens are physically destroyed. There is no soft-deleted copy and
 * no restore endpoint. Audit events, heartbeats and metrics are kept on purpose
 * so the node's history outlives it.
 *
 * CONFIRMATION: the body must carry the node's stored `region`, matched
 * case/whitespace-insensitively (it's a hand-retyped place name, not an id).
 *
 * The exception is a node whose region is null — geo is geoip-derived and
 * unresolved IPs have none. There is nothing to confirm against, so the guard is
 * skipped and the body may be empty; the response reports region_verified: false.
 * Pass `region` as null/undefined for those and this sends {}.
 *
 * Callers that need a reversible "take it out of rotation" want setNodeActive.
 *
 * @param {string} nodeId
 * @param {{ region?: string|null }} [confirmation]
 * @returns {Promise<{ hard_deleted: boolean, region_verified: boolean, warnings: string[] }>}
 * @throws  {NodesError} 400 when the region doesn't match — the stored value is
 *   never echoed back, by design, so the message cannot be used to guess it.
 */
export async function deleteNode(nodeId, { region } = {}) {
  const body = typeof region === "string" && region.trim() !== ""
    ? { region: region.trim() }
    : {};

  const res = await write(() =>
    api.delete(`/enterprise-nodes/${encodeURIComponent(nodeId)}`, body),
  );
  return {
    // The response reports hard_deleted, not `deleted`; reaching here at all
    // means the purge ran. It does NOT currently return purged/retained row
    // counts even though the OpenAPI block documents them, so nothing here or
    // above may depend on those.
    hard_deleted:    res.hard_deleted ?? true,
    region_verified: res.region_verified ?? false,
    warnings:        Array.isArray(res.warnings) ? res.warnings : [],
  };
}

/**
 * Hide a node from routing, or restore it — the REVERSIBLE counterpart to delete.
 * `false` drops it out of route selection, closes its open sessions and makes it
 * deny on its next heartbeat, without destroying anything. `true` puts it back.
 * Idempotent.
 *
 * Replaces PATCH /api/partner/v1/cloud-nodes/:nodeId, removed with the partner
 * tier. Same capability, now company-scoped like every other node route, so a
 * node belonging to another tenant answers 404 rather than toggling.
 *
 * @param {string} nodeId
 * @param {boolean} active
 * @returns {Promise<object>} the node in UI shape, plus warnings
 * @throws  {NodesError}
 */
export async function setNodeActive(nodeId, active) {
  const res = await write(() =>
    api.patch(`/enterprise-nodes/${encodeURIComponent(nodeId)}/active`, {
      active: Boolean(active),
    }),
  );
  return { ...toUiNode(res), warnings: Array.isArray(res.warnings) ? res.warnings : [] };
}

/**
 * Re-probe a node and refresh its stored status now, instead of waiting for the
 * next scheduled probe.
 * @param {string} nodeId
 * @returns {Promise<object>} the re-probed node in UI shape
 * @throws  {NodesError}
 */
export async function reprobeNode(nodeId) {
  const res = await write(() =>
    api.post(`/enterprise-nodes/${encodeURIComponent(nodeId)}/reprobe`),
  );
  return toUiNode(res);
}

// ── Node Core download ────────────────────────────────────────────────────────
// Two independent calls behind the "Download Package" UI. They are separate on
// the backend because they fail for unrelated reasons — S3/IAM vs node state —
// and they stay separate here so one failing never blocks the other. The binary
// is identical for every node; the config is per-node and carries a secret.

/** The failure classes above, mapped from the backend's machine-readable `reason`. */
function toDownloadError(err) {
  const status = err?.status ?? null;
  const reason = err?.reason ?? null;

  let message = null;
  if (status === 409 || reason === "node_online")            message = DOWNLOAD_MESSAGES.online;
  else if (status === 502 || reason === "binary_unavailable") message = DOWNLOAD_MESSAGES.unavailable;
  else if (status === 400 && reason === "unsupported_platform") message = DOWNLOAD_MESSAGES.platform;

  if (message) {
    console.warn("[nodesService:download]", status, reason, err?.message ?? err);
    return new NodesError(message, { status, detail: err?.message ?? String(err) });
  }
  // Everything else is an ordinary write failure (401/403/404/5xx/offline).
  return toWriteError(err);
}

const download = (fn) => call(fn, toDownloadError);

/**
 * A short-lived presigned S3 URL for the Node Core binary.
 *
 * READ-ONLY and side-effect free — safe to call whenever the UI needs it, and
 * safe to retry. The bucket is private; this URL is the only way in, and it is
 * itself a credential for the few minutes it lives, so it must not be logged,
 * persisted or put in a link the user can share.
 *
 * USE IT IMMEDIATELY. `expires_in_seconds` is the whole lifetime of the URL and
 * the clock starts when the backend signs it, not when the user clicks. Fetching
 * a stale one returns an S3 `AccessDenied / Request has expired` XML page rather
 * than a file. Only the initial request is signed, so a transfer already in
 * flight is unaffected and may take as long as it needs.
 *
 * `size_bytes` is advisory: it comes from an S3 HEAD and is null when that fails,
 * so it is for display only and must never gate the download.
 *
 * @param {string} nodeId
 * @param {{ platform?: "linux" }} [opts] Only linux is published today.
 * @returns {Promise<{ platform: string, binary_url: string, object_key: string,
 *                     expires_at: string, expires_in_seconds: number,
 *                     size_bytes: number|null, sha256: string|null }>}
 * @throws  {NodesError}
 */
export async function fetchNodeCoreBinaryUrl(nodeId, { platform = "linux" } = {}) {
  const qs = `?platform=${encodeURIComponent(platform)}`;
  return download(() =>
    api.get(`/enterprise-nodes/${encodeURIComponent(nodeId)}/binary-url${qs}`),
  );
}

/**
 * The Node Core configuration file for a node.
 *
 * POST, not GET, because it HAS SIDE EFFECTS: it can mint an enrollment token,
 * push the node's enrollment deadline out, and restore a node the server's expiry
 * sweep had already soft-deleted. Do not call it speculatively on render — only
 * in response to the operator asking for the file.
 *
 * It is however IDEMPOTENT within the token's lifetime: a node that still has an
 * unused, unexpired token gets that same token back (`token_reused: true`) rather
 * than a second one, so downloading twice does not invalidate the file saved the
 * first time.
 *
 * This is what replaced "the token is shown exactly once and can never be
 * recovered" — a lost or expired token is now recoverable here, for any node that
 * is not currently online.
 *
 * `config` is THE FILE, verbatim — exactly node_id, enrollment_token and
 * gateway_url. Write it to disk unchanged; the sibling fields are UI metadata and
 * must never be folded into it.
 *
 * Refused with 409 while the node is online: an enrollment token can overwrite a
 * node's identity when it enrolls, so it is only issued for nodes that are
 * pending or stopped.
 *
 * @param {string} nodeId
 * @returns {Promise<{ config: { node_id: string, enrollment_token: string, gateway_url: string },
 *                     filename: string, expires_at: string, expires_in_seconds: number,
 *                     token_reused: boolean, warnings: string[] }>}
 * @throws  {NodesError} 409 when the node is online.
 */
export async function fetchNodeCoreConfig(nodeId) {
  const res = await download(() =>
    api.post(`/enterprise-nodes/${encodeURIComponent(nodeId)}/config`),
  );
  return {
    ...res,
    filename: res.filename || "node-core.json",
    warnings: Array.isArray(res.warnings) ? res.warnings : [],
  };
}

// ── Live log stream ───────────────────────────────────────────────────────────

/**
 * Absolute URL for a node's SSE log tail, or null when unauthenticated.
 *
 * The token rides the query string because the browser's native EventSource
 * cannot set an Authorization header — the same approach the notification
 * stream uses (see notificationsService). Callers must rebuild the URL on every
 * connection attempt: the access token rotates on refresh, and a cached URL
 * would carry a dead one into the retry.
 *
 * This works now. On the old /nodes route the SSE endpoint sat behind the
 * header-only auth middleware, so a browser EventSource could never authenticate
 * and the log tail always 401'd; /enterprise-nodes puts it behind the SSE
 * middleware that reads exactly this `token` parameter.
 *
 * @param {string} nodeId
 * @param {{ level?: string }} opts
 * @returns {string|null}
 */
export function nodeLogStreamUrl(nodeId, { level } = {}) {
  const token = TokenStorage.get();
  if (!token || !nodeId) return null;

  const params = new URLSearchParams({ token });
  if (level) params.set("level", level);
  return `${BASE_URL}/enterprise-nodes/${encodeURIComponent(nodeId)}/logs/stream?${params}`;
}
